import React, {useEffect, useMemo, useState} from "react";
import {Link, useHistory, useLocation} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {actionCreators, State} from "../state";
import {bindActionCreators} from "redux";
import {eng, tr} from "../util/languageData";
import "./navigation.css";
import languageService from "../services/languageService";
import logo from "../assets/images/logo.png";
import {landingPages} from "../content/landingPages";
import {languageForPath, localizedPath} from "../util/publicRoutes";

function Navigation() {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const state = useSelector((state: State) => state);
  const {isLoggedIn, user} = state.authReducer;
  const language = state.languageReducer;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [adminDropdownOpen, setAdminDropdownOpen] = useState(false);
  const [solutionsDropdownOpen, setSolutionsDropdownOpen] = useState(false);

  // Keep the public links in the visitor's current language, always in their
  // final trailing-slash form ("/en" and "/about" 301 to "/en/" and "/about/").
  const isEnglishRoute =
    location.pathname === "/en" || location.pathname.startsWith("/en/");
  // Pages are served at the trailing-slash URL; compare active state without it.
  const currentPath = location.pathname.replace(/\/$/, "") || "/";
  const homePath = isEnglishRoute ? "/en/" : "/";
  const homeActive = currentPath === "/" || currentPath === "/en";
  const aboutPath = isEnglishRoute ? "/en/about/" : "/about/";
  const aboutActive = currentPath === "/about" || currentPath === "/en/about";
  const contactPath = isEnglishRoute ? "/en/contact/" : "/contact/";
  const contactActive = currentPath === "/contact" || currentPath === "/en/contact";
  const blogPath = isEnglishRoute ? "/en/blog/" : "/blog/";
  const blogActive = location.pathname.includes("/blog");
  const fraudPath = isEnglishRoute
    ? "/en/fraud-prevention/"
    : "/fraud-prevention/";
  const fraudActive = location.pathname.includes("/fraud-prevention");
  const pricingPath = isEnglishRoute ? "/en/pricing/" : "/fiyatlandirma/";
  const pricingActive =
    location.pathname.includes("/fiyatlandirma") ||
    location.pathname.includes("/pricing");

  const {setAppLanguage} = useMemo(
    () => bindActionCreators(actionCreators.languageActions, dispatch),
    [dispatch]
  );

  const handleLanguageChange = async (lang: "tr" | "en") => {
    localStorage.setItem("language", lang);

    if (isLoggedIn) {
      try {
        await languageService.updateUserLanguage(lang);
      } catch (error) {
        console.error("Failed to update language preference:", error);
      }
    }

    setAppLanguage(lang, lang === "tr" ? tr : eng);

    // On public pages, also move to the localized URL so the blog and other
    // pages actually render in the chosen language (their language is derived
    // from the URL). Admin/app routes return null here and stay put.
    const target = localizedPath(location.pathname, lang);
    if (target && target !== location.pathname) {
      history.push(target);
    }
  };

  useEffect(() => {
    if (!isLoggedIn) return;

    let cancelled = false;

    languageService
      .getUserLanguage()
      .then((response) => {
        if (cancelled) return;

        const userLanguage = response.data.language as "tr" | "en" | undefined;

        if (userLanguage && userLanguage !== localStorage.getItem("language")) {
          localStorage.setItem("language", userLanguage);
          // Public pages take their language from the URL (App.tsx); only the
          // app routes follow the account's language.
          if (languageForPath(window.location.pathname) === null) {
            setAppLanguage(userLanguage, userLanguage === "tr" ? tr : eng);
          }
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error("Failed to fetch user language preference:", error);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, setAppLanguage]);

  useEffect(() => {
    setSidebarOpen(false);
    setAdminDropdownOpen(false);
    setSolutionsDropdownOpen(false);
  }, [location.pathname]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const toggleAdminDropdown = (e: React.MouseEvent) => {
    e.preventDefault();
    setAdminDropdownOpen(!adminDropdownOpen);
  };

  const toggleSolutionsDropdown = (e: React.MouseEvent) => {
    e.preventDefault();
    setSolutionsDropdownOpen(!solutionsDropdownOpen);
  };

  // The public marketing/landing pages, language-aware, for the menu dropdown.
  const solutionLang: "tr" | "en" = isEnglishRoute ? "en" : "tr";
  const solutionLinks = landingPages.map((p) => ({
    to: isEnglishRoute ? `/en/${p.enSlug}/` : `/${p.slug}/`,
    label: p[solutionLang].serviceName,
    active:
      location.pathname.includes(`/${p.slug}`) ||
      location.pathname.includes(`/${p.enSlug}`),
  }));

  // The "Solutions" menu group listing every marketing/landing page. Shown in
  // both the public and the logged-in menus so the pages are always reachable.
  const solutionsDropdown = (
    <div className="sidebar-dropdown">
      <button className="sidebar-dropdown-toggle" onClick={toggleSolutionsDropdown}>
        <span className="menu-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2"/>
            <rect x="14" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2"/>
            <rect x="3" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2"/>
            <rect x="14" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2"/>
          </svg>
        </span>
        <span>{language?.data?.navigation?.solutions}</span>
        <span className={`dropdown-icon ${solutionsDropdownOpen ? 'open' : ''}`}>▼</span>
      </button>
      <div className={`sidebar-dropdown-menu ${solutionsDropdownOpen ? 'show' : ''}`}>
        {solutionLinks.map((s) => (
          <Link
            key={s.to}
            to={s.to}
            className={`sidebar-dropdown-item ${s.active ? "active" : ""}`}
            onClick={closeSidebar}
          >
            {s.label}
          </Link>
        ))}
      </div>
    </div>
  );

  // Map navigation items for more maintainable code
  const renderNavItems = () => {
    if (isLoggedIn) {
      return (
        <>
          {user?.roles?.includes("ROLE_ADMIN") && (
            <div className="sidebar-dropdown">
              <button className="sidebar-dropdown-toggle" onClick={toggleAdminDropdown}>
                <span className="menu-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
                <span>{language?.data?.navigation?.admin?.title}</span>
                <span className={`dropdown-icon ${adminDropdownOpen ? 'open' : ''}`}>▼</span>
              </button>
              <div className={`sidebar-dropdown-menu ${adminDropdownOpen ? 'show' : ''}`}>
                <Link
                  to="/admin/shift"
                  className={`sidebar-dropdown-item ${location.pathname === "/admin/shift" ? "active" : ""}`}
                  onClick={closeSidebar}
                >
                  {language?.data?.navigation?.admin?.shifts}
                </Link>
                <Link
                  to="/admin/creditcard"
                  className={`sidebar-dropdown-item ${location.pathname === "/admin/creditcard" ? "active" : ""}`}
                  onClick={closeSidebar}
                >
                  {language?.data?.navigation?.admin?.creditCards}
                </Link>
                <Link
                  to="/admin/deposit"
                  className={`sidebar-dropdown-item ${location.pathname === "/admin/deposit" ? "active" : ""}`}
                  onClick={closeSidebar}
                >
                  {language?.data?.navigation?.admin?.deposits}
                </Link>
                <Link
                  to="/admin/employeedeficit"
                  className={`sidebar-dropdown-item ${location.pathname === "/admin/employeedeficit" ? "active" : ""}`}
                  onClick={closeSidebar}
                >
                  {language?.data?.navigation?.admin?.employeeDeficit}
                </Link>
                <div className="dropdown-divider"></div>
                <Link
                  to="/admin/company/users"
                  className={`sidebar-dropdown-item ${location.pathname === "/admin/company/users" ? "active" : ""}`}
                  onClick={closeSidebar}
                >
                  {language?.data?.navigation?.admin?.companyUsers}
                </Link>
                <Link
                  to="/admin/reconciliation/v2/workspace"
                  className={`sidebar-dropdown-item ${
                    location.pathname.startsWith("/admin/reconciliation/v2") ? "active" : ""
                  }`}
                  onClick={closeSidebar}
                >
                  {language?.data.navigation?.admin?.reconciliationV2}
                </Link>
                <Link
                  to="/admin/payment-type-correction"
                  className={`sidebar-dropdown-item ${
                    location.pathname.startsWith("/admin/payment-type-correction") ? "active" : ""
                  }`}
                  onClick={closeSidebar}
                >
                  {language?.data?.navigation?.admin?.paymentTypeCorrection}
                </Link>
                <Link
                  to="/admin/finance/gross-profit"
                  className={`sidebar-dropdown-item ${
                    location.pathname.startsWith("/admin/finance/gross-profit") ? "active" : ""
                  }`}
                  onClick={closeSidebar}
                >
                  {language?.data?.navigation?.admin?.fuelGrossProfit}
                </Link>
                <Link
                  to="/admin/finance/sales"
                  className={`sidebar-dropdown-item ${
                    location.pathname.startsWith("/admin/finance/sales") ? "active" : ""
                  }`}
                  onClick={closeSidebar}
                >
                  {language?.data?.navigation?.admin?.saleEarnings}
                </Link>
                <Link
                  to="/admin/finance/settlement"
                  className={`sidebar-dropdown-item ${
                    location.pathname.startsWith("/admin/finance/settlement") ? "active" : ""
                  }`}
                  onClick={closeSidebar}
                >
                  {language?.data?.navigation?.admin?.settlementCheck}
                </Link>
              </div>
            </div>
          )}

          {user?.roles?.some((role: string) => ["ROLE_ADMIN", "ROLE_USER", "ROLE_CREDITCARD"].includes(role)) && (
            <Link
              to="/shiftreview"
              className={`sidebar-item ${location.pathname === "/shiftreview" ? "active" : ""}`}
              onClick={closeSidebar}
            >
              <span className="menu-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              <span>{language?.data?.navigation?.shiftReview}</span>
            </Link>
          )}

          {user?.roles?.some((role: string) => ["ROLE_ADMIN", "ROLE_USER", "ROLE_CASH"].includes(role)) && (
            <Link
              to="/cash"
              className={`sidebar-item ${location.pathname === "/cash" ? "active" : ""}`}
              onClick={closeSidebar}
            >
              <span className="menu-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="2" y="6" width="20" height="12" rx="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <circle cx="12" cy="12" r="2" stroke="currentColor" strokeWidth="2"/>
                  <path d="M6 10v4M18 10v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </span>
              <span>{language?.data?.navigation?.cash}</span>
            </Link>
          )}

          {user?.roles?.some((role: string) =>
            ["ROLE_ADMIN", "ROLE_USER"].includes(role)
          ) && (
            <Link
              to="/analytics/sales"
              className={`sidebar-item ${location.pathname === "/analytics/sales" ? "active" : ""}`}
              onClick={closeSidebar}
            >
              <span className="menu-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 3v18h18M7 14l4-4 3 3 5-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              <span>{language?.data?.navigation?.salesAnalytics}</span>
            </Link>
          )}

          {user?.roles?.some((role: string) =>
            ["ROLE_ADMIN", "ROLE_USER"].includes(role)
          ) && (
            <Link
              to="/analytics/live"
              className={`sidebar-item ${location.pathname === "/analytics/live" ? "active" : ""}`}
              onClick={closeSidebar}
            >
              <span className="menu-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zM12 8v4l3 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              <span>{language?.data?.navigation?.liveSales}</span>
            </Link>
          )}

          {user?.roles?.some((role: string) =>
            ["ROLE_ADMIN", "ROLE_USER"].includes(role)
          ) && (
            <Link
              to="/tanks"
              className={`sidebar-item ${location.pathname === "/tanks" ? "active" : ""}`}
              onClick={closeSidebar}
            >
              <span className="menu-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="5" y="3" width="14" height="18" rx="3" stroke="currentColor" strokeWidth="2"/>
                  <path d="M5 13c2 0 2 2 4 2s2-2 4-2 2 2 4 2 2-2 2-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </span>
              <span>{language?.data?.navigation?.tanks}</span>
            </Link>
          )}

          <Link
            to="/employeedeficit"
            className={`sidebar-item ${location.pathname === "/employeedeficit" ? "active" : ""}`}
            onClick={closeSidebar}
          >
            <span className="menu-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M9 15L15 9M9 9l6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
            </span>
            <span>{language?.data?.navigation?.admin?.employeeDeficit}</span>
          </Link>

          {user?.roles?.some((role: string) => ["ROLE_ADMIN", "ROLE_USER"].includes(role)) && (
            <Link
              to="/account/subscription"
              className={`sidebar-item ${location.pathname === "/account/subscription" ? "active" : ""}`}
              onClick={closeSidebar}
            >
              <span className="menu-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
                  <path d="M2 10h20" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </span>
              <span>{language?.data?.navigation?.mySubscription}</span>
            </Link>
          )}

          <Link
            to={aboutPath}
            className={`sidebar-item ${aboutActive ? "active" : ""}`}
            onClick={closeSidebar}
          >
            <span className="menu-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 3H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V5a2 2 0 00-2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <path d="M12 11v5M12 8h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
            <span>{language?.data?.navigation?.about}</span>
          </Link>
          {solutionsDropdown}
        </>
      );
    } else {
      return (
        <>
          <Link
            to={aboutPath}
            className={`sidebar-item ${aboutActive ? "active" : ""}`}
            onClick={closeSidebar}
          >
            <span className="menu-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 3H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V5a2 2 0 00-2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <path d="M12 11v5M12 8h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
            <span>{language?.data?.navigation?.about}</span>
          </Link>
          {solutionsDropdown}
          <Link
            to={fraudPath}
            className={`sidebar-item ${fraudActive ? "active" : ""}`}
            onClick={closeSidebar}
          >
            <span className="menu-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6l8-4z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
            <span>{language?.data?.navigation?.fraud}</span>
          </Link>
          <Link
            to={pricingPath}
            className={`sidebar-item ${pricingActive ? "active" : ""}`}
            onClick={closeSidebar}
          >
            <span className="menu-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M7 7h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </span>
            <span>{language?.data?.navigation?.pricing}</span>
          </Link>
          <Link
            to={blogPath}
            className={`sidebar-item ${blogActive ? "active" : ""}`}
            onClick={closeSidebar}
          >
            <span className="menu-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
            <span>{language?.data?.navigation?.blog}</span>
          </Link>
          <Link
            to={contactPath}
            className={`sidebar-item ${contactActive ? "active" : ""}`}
            onClick={closeSidebar}
          >
            <span className="menu-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <path d="M22 6l-10 7L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </span>
            <span>{language?.data?.navigation?.contact}</span>
          </Link>
          <Link
            to="/login"
            className={`sidebar-item ${location.pathname === "/login" ? "active" : ""}`}
            onClick={closeSidebar}
          >
            <span className="menu-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l5-5-5-5M15 12H3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
            <span>{language?.data?.navigation?.login}</span>
          </Link>
        </>
      );
    }
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={`sidebar-overlay ${sidebarOpen ? 'show' : ''}`}
        onClick={closeSidebar}
      ></div>

      {/* Modern header */}
      <header className="modern-header">
        <div className="header-container">
          <Link to={homePath} className="brand" onClick={closeSidebar}>
            <img src={logo} alt="Shift Tracker Logo" className="brand-logo" />
            <span className="brand-text">Shift Tracker</span>
          </Link>

          <div className="header-actions">
            <Link to={homePath} className="header-button home-button" onClick={closeSidebar}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9 22V12h6v10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>{language?.data?.navigation?.home}</span>
            </Link>

            {isLoggedIn ? (
              <Link to="/logout" className="logout-button" onClick={closeSidebar}>
                <span>{language?.data?.navigation?.logout}</span>
              </Link>
            ) : (
              <Link to="/login" className="header-button login-button" onClick={closeSidebar}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l5-5-5-5M15 12H3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>{language?.data?.navigation?.login}</span>
              </Link>
            )}

            <button className="menu-toggle" onClick={toggleSidebar}>
              <div className={`hamburger ${sidebarOpen ? 'active' : ''}`}>
                <span></span>
                <span></span>
                <span></span>
              </div>
            </button>
          </div>
        </div>
      </header>

      {/* Modern sidebar */}
      <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <Link to={homePath} className="sidebar-brand" onClick={closeSidebar}>
            <img src={logo} alt="Shift Tracker Logo" className="brand-logo" />
            <span className="brand-text">Shift Tracker</span>
          </Link>
          <button className="sidebar-close" onClick={closeSidebar}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        <div className="sidebar-content">
          <Link
            to={homePath}
            className={`sidebar-item ${homeActive ? "active" : ""}`}
            onClick={closeSidebar}
          >
            <span className="menu-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9 22V12h6v10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
            <span>{language?.data?.navigation?.home}</span>
          </Link>

          {renderNavItems()}
        </div>

        <div className="sidebar-footer">
          <div className="language-section">
            <div className="language-header">
              <svg className="translate-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 12H22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 2C14.5013 4.73835 15.9228 8.29203 16 12C15.9228 15.708 14.5013 19.2616 12 22C9.49872 19.2616 8.07725 15.708 8 12C8.07725 8.29203 9.49872 4.73835 12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="language-buttons">
              <button
                className={`language-btn ${language.appLanguage === "tr" ? "active" : ""}`}
                onClick={() => handleLanguageChange("tr")}
              >
                <span>🇹🇷</span>
              </button>
              <button
                className={`language-btn ${language.appLanguage === "en" ? "active" : ""}`}
                onClick={() => handleLanguageChange("en")}
              >
                <span>🇬🇧</span>
              </button>
            </div>
          </div>

          {isLoggedIn && (
            <Link to="/logout" className="logout-link" onClick={closeSidebar}>
              {language?.data?.navigation?.logout} <span className="logout-icon">→</span>
            </Link>
          )}
        </div>
      </div>
    </>
  );
}

export default Navigation;
